import type { SVGProps } from "react";

export type IconName =
  | "folder"
  | "download"
  | "upload"
  | "reset"
  | "plus"
  | "minus"
  | "expand"
  | "tray-arrow-down"
  | "doc-arrow-down"
  | "settings"
  | "close"
  | "chevron-down";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: number;
}

/**
 * SF-Symbols-inspired stroke icons.
 * 24×24 viewBox, currentColor, rounded line ends, 1.6 px stroke.
 */
export const Icon = ({ name, size = 16, strokeWidth = 1.6, ...rest }: IconProps) => {
  const common: SVGProps<SVGSVGElement> = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    ...rest,
  };

  switch (name) {
    case "folder":
      return (
        <svg {...common}>
          <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h3.4a2 2 0 0 1 1.4.6l1.4 1.4a2 2 0 0 0 1.4.6h5.4A2.5 2.5 0 0 1 21 10v7.5A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5v-10Z" />
        </svg>
      );
    case "download":
      return (
        <svg {...common}>
          <path d="M12 4v11m0 0-4-4m4 4 4-4" />
          <path d="M5 19h14" />
        </svg>
      );
    case "upload":
      return (
        <svg {...common}>
          <path d="M12 20V9m0 0-4 4m4-4 4 4" />
          <path d="M5 5h14" />
        </svg>
      );
    case "tray-arrow-down":
      return (
        <svg {...common}>
          <path d="M12 3v10m0 0-3.5-3.5M12 13l3.5-3.5" />
          <path d="M4 14v3.5A2.5 2.5 0 0 0 6.5 20h11a2.5 2.5 0 0 0 2.5-2.5V14" />
        </svg>
      );
    case "doc-arrow-down":
      return (
        <svg {...common}>
          <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
          <path d="M14 3v3.5A1.5 1.5 0 0 0 15.5 8H19" />
          <path d="M12 11v5m0 0-2-2m2 2 2-2" />
        </svg>
      );
    case "reset":
      return (
        <svg {...common}>
          <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
          <path d="M3.5 4v5h5" />
        </svg>
      );
    case "plus":
      return (
        <svg {...common} strokeWidth={1.8}>
          <path d="M12 5.5v13M5.5 12h13" />
        </svg>
      );
    case "minus":
      return (
        <svg {...common} strokeWidth={1.8}>
          <path d="M5.5 12h13" />
        </svg>
      );
    case "expand":
      return (
        <svg {...common}>
          <path d="M9 4H5a1 1 0 0 0-1 1v4M15 4h4a1 1 0 0 1 1 1v4M9 20H5a1 1 0 0 1-1-1v-4M15 20h4a1 1 0 0 0 1-1v-4" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 0 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
        </svg>
      );
    case "close":
      return (
        <svg {...common} strokeWidth={1.7}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg {...common}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
  }
};
